/**
 * Retry distribution donut, shared by the Statistics page, each endpoint's
 * Analytics tab and a stress run's detail page.
 *
 * Providers retry transient failures (429s, 503s, timeouts) internally, so a
 * call that looks like a clean success can still have cost several attempts.
 * This is the only place that shows how often that happens.
 */
import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { ApiRetryGroup } from '../types';
import {
  CHART_RETRY_COLORS,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from '../../components/stats/chartTheme';
import {
  ChartEmptyState,
  ChartPanel,
  ChartSliceGradient,
} from '../../components/stats/ChartPrimitives';
import { chartGradientFill, useChartGradientId } from '../../components/stats/chartGradient';

// Green for a first-attempt success, warming towards red as the attempts pile
// up. Anything past the palette reuses its last (worst) colour.
function colorFor(retries: number): string {
  return CHART_RETRY_COLORS[Math.min(retries, CHART_RETRY_COLORS.length - 1)];
}

function labelFor(retries: number): string {
  if (retries === 0) return 'No retries';
  return `${retries} ${retries === 1 ? 'retry' : 'retries'}`;
}

interface RetriesPanelProps {
  groups: ApiRetryGroup[] | undefined;
  loading?: boolean;
  /** Why there is nothing to show, when the window holds no measured calls. */
  emptyLabel?: string;
}

export function RetriesPanel({
  groups,
  loading = false,
  emptyLabel = 'No retry data in this period',
}: RetriesPanelProps) {
  const gradientBaseId = useChartGradientId('api-retries');
  const slices = useMemo(
    () =>
      (groups || [])
        .filter((group) => group.count > 0)
        .map((group) => ({
          key: String(group.retries),
          name: labelFor(group.retries),
          value: group.count,
          color: colorFor(group.retries),
        })),
    [groups],
  );

  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const retried = slices
    .filter((slice) => slice.key !== '0')
    .reduce((sum, slice) => sum + slice.value, 0);

  return (
    <ChartPanel
      title="Retries"
      subtitle="Attempts a call needed before it settled."
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v6h6M20 20v-6h-6M5.5 18.5a8 8 0 0 0 13-3M18.5 5.5a8 8 0 0 0-13 3" />
        </svg>
      }
      iconClassName="bg-warning-50 text-warning-700"
      summary={
        <div className="text-right shrink-0">
          <span className="text-lg font-semibold text-slate-700 tabular-nums">
            {total > 0 ? `${((retried / total) * 100).toFixed(0)}%` : '—'}
          </span>
          <span className="ml-1 text-[10px] text-slate-500 uppercase">retried</span>
        </div>
      }
    >
      {loading && total === 0 ? (
        <ChartEmptyState>Loading…</ChartEmptyState>
      ) : total === 0 ? (
        <ChartEmptyState>
          <span>
            <span className="block font-medium">Nothing measured</span>
            <span className="mt-0.5 block text-xs">{emptyLabel}</span>
          </span>
        </ChartEmptyState>
      ) : (
        <>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <defs>
                  {slices.map((slice) => (
                    <ChartSliceGradient
                      key={slice.key}
                      id={`${gradientBaseId}-${slice.key}`}
                      color={slice.color}
                    />
                  ))}
                </defs>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="52%"
                  outerRadius="80%"
                  paddingAngle={2}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {slices.map((slice) => (
                    <Cell
                      key={slice.key}
                      fill={chartGradientFill(`${gradientBaseId}-${slice.key}`)}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  formatter={(value: number, name: string) => [
                    `${value} call${value === 1 ? '' : 's'} · ${((value / total) * 100).toFixed(1)}%`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-3 space-y-1.5">
            {slices.map((slice) => (
              <li key={slice.key} className="flex items-center gap-2 text-xs">
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: slice.color }}
                />
                <span className="text-slate-600 truncate flex-1">{slice.name}</span>
                <span className="tabular-nums text-slate-500">
                  {((slice.value / total) * 100).toFixed(0)}%
                </span>
                <span className="tabular-nums font-medium text-slate-700 w-10 text-right">
                  {slice.value}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </ChartPanel>
  );
}
